import React, {useContext} from 'react';
import {StyleSheet, View} from 'react-native';
import Styles from '../../Styles';
import Localized from 'src/constants/AppStrings';
import AVText from '../AVText';
import {getDescriber as getSnackDescriber} from 'src/components/screens/sendSnack/descriptor/sendasnack/DescriptorType';
import {getFooterDescriber} from 'src/components/elements/footerDescriptor/DescriptorType';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';
import AVTouchableOpacity from 'src/components/elements/AVTouchableOpacity';
import NavActions from 'src/actions/NavActions';
import {getButtonDescriber} from 'src/components/elements/buttonDescriptor/DescriptorType';
import {ButtonType} from 'src/components/elements/RoundedButton';
import {IsConnectedProps} from 'src/types/Screen';
import withIsConnected from 'src/components/hoc/withIsConnected';
import {RewardCashType} from 'src/types/Rewards';
import AccountStore from 'src/stores/AccountStore';
import ActionsFactory from 'src/actions/ActionsFactory';
import Util from 'src/Util';
import uuid from 'src/nativeModules/UUID';
import Events from 'src/logging/Events';
import {generateErrorMessage} from 'src/logging/generateErrorMessage';
import CrashlyticsEvents from 'src/logging/Crashlytics';
import ScreenContext from 'src/components/ScreenContext';
import {adjustDefaultBalance} from 'src/redux/slices/accountSlice';
import {store} from 'src/redux/store';
import {getDescriber} from 'src/components/screens/descriptor/DescriptorType';
import AppRoutes from 'src/AppRoutes';
import CashRewardsIcon from 'src/components/img/svg/rewards/CashRewardsIcon';
import {
  withGlobalize,
  WithGlobalizeProps,
} from '../../../i18n/HOC/withGlobalize';

type CashRedeemTabProps = IsConnectedProps &
  WithGlobalizeProps & {
    cashReward: RewardCashType;
    point: number;
  };
const lineHeightMultiplier = 1.14;
const CashRedeemTab = (props: CashRedeemTabProps) => {
  const {point, cashReward} = props;
  const context = useContext(ScreenContext);

  const cashRedeemError = () => {
    NavActions.navigate(AppRoutes.ServerErrorDialog, {
      errorTitle: Localized.Labels.redeem_rewd,
      errorDesc: Localized.Errors.error_rewards_modal,
      errorBtnName: getDescriber().getGoToSettings(),
    });
  };

  const handleAddToBalance = async () => {
    context.actions.showSpinner();
    try {
      await ActionsFactory.getAccountActions().redeemCashReward(
        AccountStore.getAccountId(),
        cashReward.rewardProgramId,
        cashReward.pointsRequired,
        cashReward.cashAmount,
        Util.getCurrentDate(),
      );
      store.dispatch(
        adjustDefaultBalance({
          amount: cashReward.cashAmount,
          reason: Localized.Labels.redeem,
        }),
      );
      NavActions.popToTop();
    } catch (error) {
      const guid = await uuid.getRandomUUID();
      CrashlyticsEvents.log(
        'Exception',
        'RedeemCashRewardScreen:HandleAddToBalance',
        generateErrorMessage(error),
        guid,
      );
      Events.Error.trackEvent(
        'Exception',
        'RedeemCashRewardScreen:HandleAddToBalance',
        generateErrorMessage(error),
        guid,
      );
      cashRedeemError();
    } finally {
      context.actions.hideSpinner();
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.titleHead}>
        <AVText
          style={[
            styles.redeemRwd,
            getSnackDescriber()?.snackDetailsBtnFontSTyle(),
          ]}
          accessibilityLabel={Localized.Labels.redeem_rewd}
        >
          {Localized.Labels.redeem_rewd}
        </AVText>
      </View>
      <View>
        <AVText
          style={[
            styles.cashRedeemMessage,
            getSnackDescriber()?.snackDetailsBtnFontSTyle(),
          ]}
          accessibilityLabel={Localized.Labels.redeem_cash_description}
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
        >
          {Localized.Labels.redeem_cash_description}
        </AVText>
      </View>

      <View style={styles.cashIconView}>
        <CashRewardsIcon color={getFooterDescriber().bottomTabBtnColor()} />
      </View>

      <AVText
        style={[
          styles.productName,
          getSnackDescriber()?.snackDetailsBtnFontSTyle(),
        ]}
        maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
      >
        {Util.formatCurrency(
          props,
          cashReward.cashAmount,
          AccountStore.getCurrency(),
          {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          },
        ) +
          ' ' +
          Localized.Labels.credit}
      </AVText>
      <View style={styles.ptsView}>
        <AVText
          style={[
            styles.ptsNumber,
            getSnackDescriber()?.snackDetailsBtnFontSTyle(),
          ]}
          accessibilityLabel={point.toString()}
        >
          {Localized.Labels.formatString(Localized.Labels.points_number, point)}
        </AVText>
      </View>
      <View style={[styles.buttonsParentsView]}>
        <RoundedAppButton
          accessibilityHintValue="Double tap to add to balance"
          accessibilityLabelValue={Localized.Buttons.add_to_balance}
          aria-label={Localized.Buttons.add_to_balance}
          titleText={Localized.Buttons.add_to_balance}
          buttonType={RoundedAppButtonType.Outline}
          buttonContainerStyle={styles.buttonContainer}
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
          onPress={() => handleAddToBalance()}
          buttonViewStyle={styles.buttonViewStyle}
          textStyle={[
            styles.buttonTextStyle,
            getSnackDescriber()?.snackDetailsBtnFontSTyle(),
          ]}
        />
      </View>
      <AVTouchableOpacity
        onPress={() => NavActions.pop()}
        testID="cancelcashredeem"
        style={[styles.cancelButtonContainer]}
      >
        <View
          style={[
            {
              borderBottomColor: getButtonDescriber().checkRoundedBtnColor(
                ButtonType.outline,
              ),
            },
            styles.cancelButtonView,
          ]}
        >
          <AVText
            style={[
              styles.canceltxt,
              {
                color: getButtonDescriber().checkRoundedBtnColor(
                  ButtonType.outline,
                ),
              },
              getSnackDescriber()?.snackDetailsBtnFontSTyle(),
            ]}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm5}
          >
            {Localized.Buttons.cancel}
          </AVText>
        </View>
      </AVTouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: Styles.Spacing.m3,
    gap: Styles.Spacing.m3,
    alignItems: 'center',
    justifyContent: 'center',
    paddingBottom: Styles.Spacing.m4,
    paddingHorizontal: 16,
    paddingTop: 8,
  },
  titleHead: {
    gap: Styles.Spacing.m1,
  },
  price: {
    fontSize: Styles.Fonts.f7,
    fontWeight: '700',
    textAlign: 'center',
    color: getFooterDescriber().bottomTabBtnColor(),
    width: 66,
  },
  cashIconView: {
    width: 67,
    height: 67,
    borderRadius: 33.5,
    alignItem: 'center',
    justifyContent: 'center',
    marginTop: Styles.Spacing.m3,
  },
  productName: {
    marginHorizontal: Styles.Spacing.m3,
    textAlign: 'center',
    fontSize: Styles.Fonts.sectionHeader,
    fontWeight: '700',
    marginTop: Styles.Spacing.m0,
  },
  cashRedeemMessage: {
    fontSize: Styles.Fonts.f1,
    fontWeight: '400',
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: Styles.Spacing.m2,
    marginHorizontal: Styles.Spacing.m2,
  },
  canceltxt: {
    fontSize: Styles.Fonts.f1,
    textAlign: 'center',
    fontFamily: Styles.FontFamily.aeonikRegular,
    fontWeight: '700',
    textDecorationLine: 'none',
    paddingTop: Styles.Spacing.m2,
  },
  redeemRwd: {
    fontSize: Styles.Fonts.f1 + 6,
    fontWeight: '700',
    lineHeight: (Styles.Fonts.f1 + 6) * lineHeightMultiplier,
  },
  ptsNumber: {
    fontSize: Styles.Fonts.f1,
    fontWeight: '400',
  },
  buttonContainer: {
    overflow: 'hidden',
    alignSelf: 'center',
    justifyContent: 'center',
    alignContent: 'center',
  },
  ptsView: {
    top: -8,
  },
  buttonViewStyle: {
    overflow: 'hidden',
    width: 220,
    height: 42,
    borderWidth: 2,
  },
  buttonTextStyle: {
    fontWeight: '700',
    fontSize: Styles.Fonts.f1,
    overflow: 'hidden',
  },
  buttonsParentsView: {
    justifyContent: 'center',
    alignSelf: 'center',
    alignContent: 'center',
  },
  cancelButtonContainer: {
    justifyContent: 'center',
    alignSelf: 'center',
    alignContent: 'center',
  },
  cancelButtonView: {
    alignSelf: 'flex-start',
    borderBottomWidth: 1.5,
  },
});

export default withGlobalize(withIsConnected(CashRedeemTab));
